import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { PopoverPortalTarget } from '@/components/ui/popover-portal';
import { TipView, type TipViewMode } from '@/components/ui/tip-view';
import { getTipDefinition, type TipId } from '@/tips';

export interface TipProps {
  tipId: TipId;
  /** Override catalog mode for a specific placement. */
  mode?: TipViewMode;
  /** Required for `label` mode — the labelled text. */
  children?: ReactNode;
  popoverPortal?: PopoverPortalTarget;
  className?: string;
  id?: string;
}

/**
 * Resolve a catalog tip and render it. Pages place tips by id only.
 */
export const Tip = ({ tipId, mode, children, popoverPortal, className, id }: TipProps) => {
  const { t } = useTranslation('tips');
  const definition = getTipDefinition(tipId);
  if (!definition) {
    if (import.meta.env.DEV) {
      console.warn(`Tip "${tipId}" is missing from the tip catalog`);
    }
    return null;
  }
  const resolvedMode = mode ?? definition.mode;

  return (
    <TipView
      mode={resolvedMode}
      content={t(tipId)}
      moreAboutLabel={t('moreAbout')}
      popoverPortal={popoverPortal}
      className={className}
      id={id}
    >
      {children}
    </TipView>
  );
};

/** Translated tip body for hosts that need the string (e.g. aria / legacy hint). */
export const useTipContent = (tipId: TipId): { mode: TipViewMode; content: string } => {
  const { t } = useTranslation('tips');
  return {
    mode: getTipDefinition(tipId).mode,
    content: t(tipId),
  };
};
